import { CURRENCIES } from '../../../../configurations/currencies';
import { logger } from '../../../../core/Log/logger';
import { Picture, PICTURE_ORIGIN } from '../../../../domains/Picture/Picture';
import { extractSlugFromURL } from '../../../../shared/utilities/extractSlugFromURL';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';
import { Agency } from '../../../agency/domain/types/Agency';
import { Currency } from '../../../currency/domain/types/currency';
import { Destination } from '../../../destination/domain/types/destination';
import { PointOfInterest } from '../../../pointOfInterest/domain/types/PointOfInterest';
import { Price } from '../../../shared/types/Price';
import { Itinerary } from '../../domain/types/Itinerary';

type NormalizedPointOfInterestFromPyrite = PickDomain<PointOfInterest, 'name'>;

type NormalizedPictureFromPyrite = PickDomain<Picture, 'id' | 'author' | 'legend' | 'origin'>;

type NormalizedDestinationFromPyrite = PickDomain<
    Destination,
    'id' | 'name' | 'slug' | 'preposition'
>;

type NormalizedAgencyFromPyrite = PickDomain<Agency, 'name'>;

export type NormalizedWishListItineraryFromPyrite = PickDomain<
    Itinerary,
    'id' | 'title' | 'duration' | 'startingPrice' | 'slug',
    {
        pointsOfInterest: NormalizedPointOfInterestFromPyrite[];
        picture: NormalizedPictureFromPyrite | null;
        destination: NormalizedDestinationFromPyrite;
        agency: NormalizedAgencyFromPyrite;
    }
>;

export type WishListItineraryFromPyrite = {
    id: number;
    title: string;
    price: string;
    duration: number;
    url: string;
    pois: Array<string>;
    picture: {
        id: string;
        copyright: string;
        legend: string | null;
        width: number | null;
        height: number | null;
        origin: string;
    } | null;
    agent: {
        name: string;
        facePictureId: number;
    };
    destination: {
        id: number;
        name: string;
        preposition: string;
        slug: string;
        pageUrl: string;
    };
    travelTypes: Array<{
        name: string;
        slug: string;
    }>;
};

const CURRENCY_MAP: Record<string, Currency> = {
    // Basic symbols
    '€': 'EUR',
    '£': 'GBP',
    $: 'USD',

    // API-specific patterns
    $US: 'USD',
    $AU: 'AUD',
    $CA: 'CAD',
    $AR: 'ARS',
    R$: 'BRL',
    '£GB': 'GBP',

    // Alternative formats
    AU$: 'AUD',
    CA$: 'CAD',

    // ISO codes
    USD: 'USD',
    EUR: 'EUR',
    GBP: 'GBP',
    CAD: 'CAD',
    AUD: 'AUD',
    CHF: 'CHF',
    JPY: 'JPY',
    BRL: 'BRL',
    ARS: 'ARS',
    SEK: 'SEK',
    DKK: 'DKK',
};

function isCurrency(value: string): value is Currency {
    return CURRENCIES.includes(value as Currency);
}

function extractCurrencyAndValue(
    cleanedPriceString: string,
): { currency: Currency; valueStr: string } | null {
    // Currency at beginning: £1,234 or $1,234 or CHF 1,234
    const beforeMatch = cleanedPriceString.match(
        /^([£$€]|[A-Z]{3}|\$US|\$AU|\$CA|\$AR|R\$|£GB)\s*(.+)$/,
    );
    if (beforeMatch) {
        const [, symbol, value] = beforeMatch;
        const currency = CURRENCY_MAP[symbol] || (isCurrency(symbol) ? symbol : null);
        if (currency) return { currency, valueStr: value };
    }

    // Currency at end: 1,234 € or 1,234 $US or 1,234 USD
    const afterMatch = cleanedPriceString.match(
        /^(.+?)\s*([€£$]|\$US|\$AU|\$CA|\$AR|R\$|£GB|AU\$|CA\$|[A-Z]{3})$/,
    );
    if (afterMatch) {
        const [, value, symbol] = afterMatch;
        const currency = CURRENCY_MAP[symbol] || (isCurrency(symbol) ? symbol : null);
        if (currency) return { currency, valueStr: value };
    }

    return null;
}

function cleanNumericValue(valueStr: string): string | null {
    if (!valueStr?.trim()) return null;

    const cleaned = valueStr
        .trim()
        .replace(/[\s\u00a0\u202f\u2009]/g, '') // Remove all spaces
        .replace(/'/g, ''); // Remove apostrophes

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    // European format: comma as decimal (1.234,56)
    if (lastComma > lastDot && /,\d{1,2}$/.test(cleaned)) {
        return (
            cleaned.substring(0, lastComma).replace(/[,.]/g, '') +
            '.' +
            cleaned.substring(lastComma + 1)
        );
    }

    // Anglo format: dot as decimal (1,234.56)
    if (lastDot > lastComma && /\.\d{1,2}$/.test(cleaned)) {
        return cleaned.substring(0, lastDot).replace(/[,.]/g, '') + cleaned.substring(lastDot);
    }

    // No decimals - remove all punctuation
    return cleaned.replace(/[,.]/g, '') || null;
}

function isValidPriceValue(value: number): boolean {
    return !Number.isNaN(value) && Number.isFinite(value) && value >= 0 && value <= 10000000;
}

function createEmptyPriceRecord(): Record<Currency, number> & { base: number } {
    const record: Record<Currency, number> = Object.assign(
        {},
        ...CURRENCIES.map((currency) => ({ [currency]: 0 })),
    );
    return { ...record, base: 0 };
}

function createPriceRecord(currency: Currency, value: number): Price {
    const record = createEmptyPriceRecord();
    record[currency] = value;
    record.base = value;
    return record;
}

export function normalizePriceFromPyrite(rawPrice: string): Price {
    // Normalize all unicode spaces to regular space
    const priceString = rawPrice.replace(/[\u202f\u00a0\u2009]/g, ' ').trim();

    if (!priceString) {
        logger.warning('normalizePriceFromPyrite: empty or null input', { rawPrice });
        return createEmptyPriceRecord();
    }

    const currencyData = extractCurrencyAndValue(priceString);
    if (!currencyData) {
        logger.warning('normalizePriceFromPyrite: no currency found', {
            rawPrice,
            normalized: priceString,
        });
        return createEmptyPriceRecord();
    }

    const { currency, valueStr } = currencyData;

    const cleanedValue = cleanNumericValue(valueStr);
    if (!cleanedValue) {
        logger.warning('normalizePriceFromPyrite: could not clean numeric value', {
            rawPrice,
            valueStr,
        });
        return createEmptyPriceRecord();
    }

    const numericValue = Number.parseFloat(cleanedValue);
    if (!isValidPriceValue(numericValue)) {
        logger.warning('normalizePriceFromPyrite: invalid numeric value', {
            rawPrice,
            cleanedValue,
            numericValue,
        });
        return createEmptyPriceRecord();
    }

    return createPriceRecord(currency, numericValue);
}

export function normalizeWishListItinerary(
    itinerary: WishListItineraryFromPyrite,
): NormalizedWishListItineraryFromPyrite {
    const normalizedPicture = itinerary.picture
        ? {
              id: itinerary.picture.id.toString(),
              author: itinerary.picture.copyright,
              legend: itinerary.picture.legend ?? '',
              origin: PICTURE_ORIGIN.offerManager,
          }
        : null;

    const normalizedDestination = {
        id: itinerary.destination.id,
        name: itinerary.destination.name,
        slug: itinerary.destination.slug,
        preposition: itinerary.destination.preposition,
    };

    const normalizedAgency = {
        name: itinerary.agent.name,
    };

    return {
        id: itinerary.id,
        title: itinerary.title,
        slug: extractSlugFromURL(itinerary.url),
        duration: itinerary.duration,
        startingPrice: normalizePriceFromPyrite(itinerary.price),
        pointsOfInterest: itinerary.pois.map((name) => {
            return { name };
        }),
        picture: normalizedPicture,
        destination: normalizedDestination,
        agency: normalizedAgency,
    };
}
